import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Play } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import { systemSettingsApi } from '@/api/endpoints'
import { playCriticalSound, prepareCriticalSound, unlockAlertSound } from '@/lib/alertSound'
import { useDisplayPrefs } from '@/store/displayPrefsStore'
import { DEFAULT_CRITICAL_SOUND_SETTINGS, type CriticalSoundSettings } from '@/types'
import { SectionSkeleton, SectionError, SaveStatus } from './SectionState'
import { criticalSoundQuery } from './prefetch'

// ============================================================================
// Critical alert sound (#294 redesign, CP 294 Section 2) — super admin, one setting for every user
// ============================================================================
// Which sound plays, how often it repeats and when it stops repeating. Each change saves only that key; the
// choice shows at once with "Saving…", then "Saved"; a failed save puts the previous choice back with an error
// toast. The section reads the cache every dashboard's sound engine reads (useCriticalSoundSettings), so a
// saved change reaches this dashboard at once and the others within a minute. Each person still sets their own
// volume under Sounds & display; Play here uses this browser's critical volume.
// #338 (client request via owner 2026-10-13): the client's own recording, first and the default, and a 5 s repeat.

type Key = keyof CriticalSoundSettings

const SOUND_OPTIONS = [
  { value: 'client', label: 'criticalSound.sounds.client' },
  { value: 'hazard', label: 'criticalSound.sounds.hazard' },
  { value: 'siren', label: 'criticalSound.sounds.siren' },
  { value: 'horn', label: 'criticalSound.sounds.horn' },
] as const satisfies ReadonlyArray<{ value: CriticalSoundSettings['sound']; label: string }>

const REPEAT_OPTIONS = [
  { value: 5, label: 'criticalSound.repeats.s5' },
  { value: 10, label: 'criticalSound.repeats.s10' },
  { value: 30, label: 'criticalSound.repeats.s30' },
  { value: 60, label: 'criticalSound.repeats.s60' },
  { value: 0, label: 'criticalSound.repeats.once' },
] as const satisfies ReadonlyArray<{ value: CriticalSoundSettings['repeat_seconds']; label: string }>

// In time order, as in the approved preview.
const STOP_OPTIONS = [
  { value: 60, label: 'criticalSound.stops.m1' },
  { value: 300, label: 'criticalSound.stops.m5' },
  { value: 600, label: 'criticalSound.stops.m10' },
  { value: 0, label: 'criticalSound.stops.untilAck' },
] as const satisfies ReadonlyArray<{ value: CriticalSoundSettings['stop_after_seconds']; label: string }>

/** The translation keys of the pill choices (typed, so a missing key fails tsc). */
type ChoiceLabel = (typeof REPEAT_OPTIONS)[number]['label'] | (typeof STOP_OPTIONS)[number]['label']

function choice<T>(options: ReadonlyArray<{ value: T }>, value: unknown, fallback: T): T {
  return options.some((o) => o.value === value) ? (value as T) : fallback
}

/** What the dashboards actually use: a value outside the choices reads as that key's default (the same rule
 *  as useCriticalSoundSettings), so the section never shows a choice nobody hears. */
function effective(data: CriticalSoundSettings): CriticalSoundSettings {
  const d = DEFAULT_CRITICAL_SOUND_SETTINGS
  return {
    sound: choice(SOUND_OPTIONS, data.sound, d.sound),
    repeat_seconds: choice(REPEAT_OPTIONS, data.repeat_seconds, d.repeat_seconds),
    stop_after_seconds: choice(STOP_OPTIONS, data.stop_after_seconds, d.stop_after_seconds),
  }
}

// Saves that overlap (arrow keys through the radios, Sound then Repeat): each save gets a number per key, and an
// answer only counts while no later save for that key exists. The last save to settle re-reads the server.
interface SaveBook { latest: Partial<Record<Key, number>>; counter: number; inFlight: number }
const books = new WeakMap<object, SaveBook>()
function bookFor(client: object): SaveBook {
  let book = books.get(client)
  if (!book) { book = { latest: {}, counter: 0, inFlight: 0 }; books.set(client, book) }
  return book
}

/** Save one key: shown at once, sent alone, put back on failure. One mutation per key, so each fieldset has
 *  its own "Saving… / Saved". A response applies only its own key, and only if no later save for that key was
 *  made; a failure puts back only its own key under the same rule. */
function useSaveKey<K extends Key>(key: K) {
  const { t } = useTranslation('settings')
  const queryClient = useQueryClient()
  const queryKey = criticalSoundQuery.queryKey
  return useMutation({
    mutationFn: (value: CriticalSoundSettings[K]) =>
      systemSettingsApi.updateCriticalSound({ [key]: value } as Partial<CriticalSoundSettings>),
    onMutate: async (value) => {
      const book = bookFor(queryClient)
      const seq = ++book.counter
      book.latest[key] = seq
      book.inFlight += 1
      // A poll answering now must not overwrite the new choice with the old one.
      await queryClient.cancelQueries({ queryKey })
      const before = queryClient.getQueryData<CriticalSoundSettings>(queryKey)
      if (before) queryClient.setQueryData<CriticalSoundSettings>(queryKey, { ...before, [key]: value })
      return { previous: before?.[key], seq }
    },
    onError: (_error, _value, context) => {
      // Put this key's previous value back, unless a later save for the key exists (its choice is the newer one).
      if (context && context.previous !== undefined && bookFor(queryClient).latest[key] === context.seq) {
        const previous = context.previous
        queryClient.setQueryData<CriticalSoundSettings>(queryKey, (current) =>
          current ? { ...current, [key]: previous } : current)
      }
      toast.error(t('criticalSound.saveFailed'))
    },
    onSuccess: (saved, _value, context) => {
      if (bookFor(queryClient).latest[key] !== context?.seq) return
      queryClient.setQueryData<CriticalSoundSettings>(queryKey, (current) =>
        current ? { ...current, [key]: saved[key] } : saved)
    },
    onSettled: () => {
      const book = bookFor(queryClient)
      book.inFlight = Math.max(0, book.inFlight - 1)
      // The last save to settle ends the cache on the server's value.
      if (book.inFlight === 0) void queryClient.invalidateQueries({ queryKey })
    },
  })
}

// A floated legend is laid out inside the padding like any block (an unfloated one sits in the top border);
// the content after it clears the float. The "Saving… / Saved" note sits at the legend's right.
const FIELDSET = 'relative min-w-0 border-0 p-4'
const LEGEND = 'float-left mb-2 w-full pr-20 text-sm font-medium'
const STATUS = 'absolute right-4 top-4'

/** A row of pill-shaped radio choices (Repeat every, Stop repeating after). */
function SegmentedChoices<T extends number>({
  name, options, value, defaultValue, onChange,
}: {
  name: string
  options: ReadonlyArray<{ value: T; label: ChoiceLabel }>
  value: T
  defaultValue: T
  onChange: (value: T) => void
}) {
  const { t } = useTranslation('settings')
  return (
    <div className="clear-both flex flex-wrap gap-1.5">
      {options.map((o) => {
        const id = `${name}-${o.value}`
        return (
          <span key={o.value} className="relative">
            <input
              type="radio"
              id={id}
              name={name}
              value={o.value}
              checked={value === o.value}
              onChange={() => onChange(o.value)}
              className="peer sr-only"
            />
            <label
              htmlFor={id}
              className={cn(
                'inline-flex h-8 cursor-pointer items-center whitespace-nowrap rounded-md border bg-card px-3 text-[13px] font-medium',
                'peer-checked:border-transparent peer-checked:bg-amber-400 peer-checked:text-amber-950',
                'peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-background',
              )}
            >
              {t(o.label)}
              {o.value === defaultValue && (
                <>{' '}<span className="opacity-80">{t('criticalSound.default')}</span></>
              )}
            </label>
          </span>
        )
      })}
    </div>
  )
}

export function CriticalSoundSection() {
  const { t } = useTranslation('settings')
  const criticalVolume = useDisplayPrefs((s) => s.criticalVolume)
  const { data, isPending, isError, refetch } = useQuery(criticalSoundQuery)
  const saveSound = useSaveKey('sound')
  const saveRepeat = useSaveKey('repeat_seconds')
  const saveStop = useSaveKey('stop_after_seconds')
  const d = DEFAULT_CRITICAL_SOUND_SETTINGS

  // A click is a gesture: unlock the sound first, then play that sound at this browser's critical volume. #338: the
  // client's recording is a file, so wait for it to load (at once when it is cached) and the first click plays the
  // recording itself; if it cannot load, the engine plays its fallback (never silent).
  const play = (kind: CriticalSoundSettings['sound']) => {
    void unlockAlertSound()
      .then(() => prepareCriticalSound(kind))
      .then(() => playCriticalSound(kind, criticalVolume / 100))
  }

  const current = data ? effective(data) : null

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-base font-semibold">{t('criticalSound.title')}</h2>
        <p className="text-xs text-muted-foreground">{t('criticalSound.description')}</p>
      </div>

      {isPending ? (
        <SectionSkeleton rows={3} />
      ) : isError && !current ? (
        <SectionError onRetry={() => refetch()} />
      ) : current && (
        <div className="divide-y rounded-lg border bg-card shadow-sm">
          <fieldset className={FIELDSET}>
            <legend className={LEGEND}>{t('criticalSound.sound')}</legend>
            <span className={STATUS}><SaveStatus pending={saveSound.isPending} saved={saveSound.isSuccess} /></span>
            <div className="clear-both flex flex-col gap-1.5">
              {SOUND_OPTIONS.map((o) => {
                const id = `critical-sound-${o.value}`
                const name = t(o.label)
                return (
                  <div
                    key={o.value}
                    className="flex items-center gap-2.5 rounded-md border px-2.5 py-2 has-[:checked]:border-primary has-[:checked]:bg-primary/10"
                  >
                    <input
                      type="radio"
                      id={id}
                      name="critical-sound"
                      value={o.value}
                      checked={current.sound === o.value}
                      onChange={() => saveSound.mutate(o.value)}
                      className="h-4 w-4 shrink-0 accent-primary"
                    />
                    <label htmlFor={id} className="min-w-0 flex-1 cursor-pointer text-sm">
                      {name}
                      {o.value === d.sound && (
                        <>{' '}<span className="text-xs text-muted-foreground">{t('criticalSound.default')}</span></>
                      )}
                    </label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 shrink-0"
                      aria-label={t('criticalSound.playSound', { name })}
                      onClick={() => play(o.value)}
                    >
                      <Play className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                      {t('criticalSound.play')}
                    </Button>
                  </div>
                )
              })}
            </div>
          </fieldset>

          <fieldset className={FIELDSET}>
            <legend className={LEGEND}>{t('criticalSound.repeat')}</legend>
            <span className={STATUS}><SaveStatus pending={saveRepeat.isPending} saved={saveRepeat.isSuccess} /></span>
            <SegmentedChoices
              name="critical-repeat"
              options={REPEAT_OPTIONS}
              value={current.repeat_seconds}
              defaultValue={d.repeat_seconds}
              onChange={(v) => saveRepeat.mutate(v)}
            />
          </fieldset>

          <fieldset className={FIELDSET}>
            <legend className={LEGEND}>{t('criticalSound.stopAfter')}</legend>
            <span className={STATUS}><SaveStatus pending={saveStop.isPending} saved={saveStop.isSuccess} /></span>
            <SegmentedChoices
              name="critical-stop"
              options={STOP_OPTIONS}
              value={current.stop_after_seconds}
              defaultValue={d.stop_after_seconds}
              onChange={(v) => saveStop.mutate(v)}
            />
          </fieldset>
        </div>
      )}
    </section>
  )
}
